import {
  isWorkbookErrorCell,
  type WorkbookCell,
  type WorkbookErrorCell,
  type WorkbookPivotSummary,
} from "@erp/shared-types";
import type { ColumnType } from "../formula";
import { isEmptyCell } from "./cells";
import {
  add,
  compare,
  divide,
  fromInteger,
  parseDecimal,
  ZERO,
  type Decimal,
} from "./decimal";

/** A summary's outcome: a number, or the error a spreadsheet would show. */
export type Summary =
  | { kind: "number"; value: Decimal }
  | { kind: "error"; cell: WorkbookErrorCell };

/**
 * Everything any summary of a Value field needs from a group's cells, so a
 * group can be summarized from these alone — computed here from its cells,
 * or by the engine's SQL over millions of rows. Numbers are the cells a
 * spreadsheet's SUM reads (Text and Number Fields only).
 */
export interface ValueStats {
  /** Cells holding a number (COUNT). */
  numbers: number;
  /** Cells that aren't Empty, Error cells included (COUNTA). */
  nonEmpty: number;
  /** Distinct values among cells that are neither Empty nor errors (COUNTUNIQUE). */
  unique: number;
  /** The first Error cell, in input order; it is every numeric summary. */
  firstError: WorkbookErrorCell | null;
  sum: Decimal;
  min: Decimal | null;
  max: Decimal | null;
  /** The lower and upper middle numbers (the same one for an odd count). */
  middle: [Decimal, Decimal] | null;
}

/** Whether a Field's cells can hold numbers a summary reads. */
export function readsNumbers(type: ColumnType): boolean {
  return type === "decimal" || type === "text";
}

/** A number a cell holds, as a summary reads it; null when it holds none. */
export function cellNumber(
  cell: WorkbookCell,
  type: ColumnType,
): Decimal | null {
  if (isWorkbookErrorCell(cell) || cell === null || !readsNumbers(type))
    return null;
  return parseDecimal(String(cell));
}

/** A group's statistics for one Value field, from its cells in input order. */
export function valueStats(
  cells: readonly WorkbookCell[],
  type: ColumnType,
): ValueStats {
  const numbers: Decimal[] = [];
  const unique = new Set<string>();
  let nonEmpty = 0;
  let firstError: WorkbookErrorCell | null = null;
  for (const cell of cells) {
    if (!isEmptyCell(cell, type)) nonEmpty += 1;
    if (isWorkbookErrorCell(cell)) {
      firstError ??= cell;
      continue;
    }
    if (!isEmptyCell(cell, type)) unique.add(String(cell));
    const value = cellNumber(cell, type);
    if (value) numbers.push(value);
  }
  const sorted = [...numbers].sort(compare);
  const upper = sorted[Math.floor(sorted.length / 2)];
  const lower = sorted[Math.floor((sorted.length - 1) / 2)];
  return {
    numbers: numbers.length,
    nonEmpty,
    unique: unique.size,
    firstError,
    sum: numbers.reduce(add, ZERO),
    min: sorted[0] ?? null,
    max: sorted.at(-1) ?? null,
    middle: lower && upper ? [lower, upper] : null,
  };
}

const number = (value: Decimal): Summary => ({ kind: "number", value });

/** What summarizing no numbers gives: AVERAGE #DIV/0!, MEDIAN #NUM!, the rest 0. */
function ofNothing(summarize: WorkbookPivotSummary, field: string): Summary {
  if (summarize === "average")
    return {
      kind: "error",
      cell: { error: "#DIV/0!", cause: `No numbers in '${field}' to average` },
    };
  if (summarize === "median")
    return {
      kind: "error",
      cell: { error: "#NUM!", cause: `No numbers in '${field}' for a median` },
    };
  return number(ZERO);
}

/**
 * Summarizes a Value field's group from its statistics. COUNT counts numbers
 * and COUNTA non-empty cells (Error cells included); COUNTUNIQUE counts
 * distinct non-empty values, skipping Error cells. The rest read numbers and
 * give the group's first Error cell, as a spreadsheet's SUM does.
 */
export function summarizeStats(
  summarize: WorkbookPivotSummary,
  stats: ValueStats,
  field: string,
): Summary {
  if (summarize === "count") return number(fromInteger(stats.numbers));
  if (summarize === "counta") return number(fromInteger(stats.nonEmpty));
  if (summarize === "countunique") return number(fromInteger(stats.unique));
  if (stats.firstError) return { kind: "error", cell: stats.firstError };
  if (!stats.numbers) return ofNothing(summarize, field);
  switch (summarize) {
    case "sum":
      return number(stats.sum);
    case "average":
      return number(divide(stats.sum, fromInteger(stats.numbers)));
    case "min":
      return number(stats.min ?? ZERO);
    case "max":
      return number(stats.max ?? ZERO);
    case "median": {
      const [lower, upper] = stats.middle ?? [ZERO, ZERO];
      return number(
        compare(lower, upper) === 0
          ? upper
          : divide(add(lower, upper), fromInteger(2)),
      );
    }
  }
}
